/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, NgZone} from '@angular/core';
import template from './coupon-edit-page.component.html';
import * as _ from 'lodash';
import * as moment from 'moment';
import 'moment-timezone';
import {AppEventBusService} from '../../common/events/app-event-bus.service';

import {AdminResourceEditPageBase} from '../../common/controller/admin-resource-edit-page-base';
import {parseExpiryFields, validityCalculation} from './coupon-commons';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {SearchResource} from '../../../components/api/resources/resource/search';
import {UserProfileSaverService} from '../../../components/session/user-profile-saver.service';

@Component({
  standalone: false,
  selector: 'adn-coupon-edit-page',
  template
})
export class CouponEditPageComponent extends AdminResourceEditPageBase {
  readonly expiryUnits = ['Days', 'Months', 'Years'];

  productResults: any[] = [];

  constructor(
    eventBus: AppEventBusService,
    @Inject(UserProfileSaverService) UserProfileSaver: any,
    @Inject(LocalNetworkProfile) protected LocalNetworkProfile: any,
    @Inject(SearchResource) private searchResource: any,
    ngZone: NgZone
  ) {
    super(eventBus, UserProfileSaver, LocalNetworkProfile, ngZone);
  }

  isClaimableAt(value: string): boolean {
    return (this.editingModel.claimableAts || []).includes(value);
  }

  onClaimableChange(value: string, event: Event): void {
    this.setClaimableAt(value, (event.target as HTMLInputElement).checked);
  }

  setClaimableAt(value: string, checked: boolean): void {
    let claimableAts = [...(this.editingModel.claimableAts || [])];
    if (checked && !claimableAts.includes(value)) {
      claimableAts.push(value);
    }
    if (!checked) {
      claimableAts = claimableAts.filter((entry) => entry !== value);
    }
    this.editingModel.claimableAts = claimableAts;
    this.markExternalFormDirty();
  }

  onFieldChange(): void {
    this.markExternalFormDirty();
  }

  queryProducts(searchElement: any): void {
    this.searchResource.query({
      q: searchElement.search,
      types: 'Product',
      objectState: 'ACTIVE'
    }).then((data: any) => {
      this.productResults = data.searchResults;
    });
  }

  protected afterInitEditingModel(): void {
    if (this.isNew && !this.editingModel.validFrom) {
      const networkTimezone = this.LocalNetworkProfile.get('timeZone') || 'utc';
      this.editingModel.validFrom = moment().tz(networkTimezone).startOf('day').toDate();
      this.editingModel.validTo = moment().tz(networkTimezone).add(30, 'day').endOf('day').toDate();
    }

    this.editingModel.type = this.editingModel.type || 'MONETARY';
    this.editingModel.couponStatus = this.editingModel.couponStatus || 'PENDING';
    this.editingModel.oneTimeValidity = this.editingModel.oneTimeValidity ?? true;
    this.editingModel.allProducts = _.get(this.editingModel, ['products'], []).length < 1;
    this.editingModel.couponEndDateType = this.editingModel.couponEndDateType || 'VALID_TO';
    this.editingModel.claimableAts = this.editingModel.claimableAts || ['CAMPAIGN_CREATION'];

    if (this.editingModel.validFrom) {
      this.editingModel.modValidFrom = this.editingModel.validFrom;
    }
    if (this.editingModel.validTo) {
      this.editingModel.modValidTo = this.editingModel.validTo;
    }

    parseExpiryFields(this.editingModel);
    validityCalculation(this.editingModel);
  }

  protected onBeforeSubmit(): void {
    this.editingModel.validFrom = _.isDate(this.editingModel.modValidFrom) ? this.editingModel.modValidFrom : null;
    this.editingModel.validTo = _.isDate(this.editingModel.modValidTo) ? this.editingModel.modValidTo : null;
    this.editingModel.expiry = this.editingModel.expiryNumber > 0 && this.editingModel.expiryUnit
      ? 'P' + this.editingModel.expiryNumber + this.editingModel.expiryUnit.charAt(0) : null;

    if (this.editingModel.allProducts) {
      this.editingModel.products = [];
    }
  }

  protected afterInitAfterSave(): void {
    this.editingModel.allProducts = _.get(this.editingModel, ['products'], []).length < 1;
    validityCalculation(this.editingModel);
  }
}
